import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (!user.email_verified) {
        navigate(
          `/verify-email?email=${encodeURIComponent(user.email)}&next=${encodeURIComponent(next)}`
        );
        return;
      }
      navigate(next);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testid="login-card" title="Accedi" subtitle="AMOCANIAMOGATTI">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">E-MAIL</Label>
          <Input
            id="email"
            data-testid="login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="password">PASSWORD</Label>
          <div className="relative">
            <Input
              id="password"
              data-testid="login-password-input"
              type={showPw ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              data-testid="login-password-toggle"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "Nascondi password" : "Mostra password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9a8f83] hover:text-[#4a433c]"
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        <Button
          type="submit"
          data-testid="login-submit-btn"
          disabled={loading}
          className="w-full bg-[#e8836b] hover:bg-[#d76f57] text-white rounded-full py-6 text-sm font-semibold disabled:opacity-50"
        >
          {loading ? "Accesso…" : "ACCEDI"}
        </Button>
      </form>
      <div className="flex flex-col items-center gap-2 mt-4 text-sm">
        <Link
          data-testid="login-forgot-link"
          to="/forgot-password"
          className="text-[#c85f45] font-semibold"
        >
          PASSWORD DIMENTICATA?
        </Link>
        <span className="text-[#7a6f64]">
          Non hai un account?{" "}
          <Link data-testid="login-to-register-link" to="/register" className="text-[#c85f45] font-semibold">
            Registrati
          </Link>
        </span>
      </div>
    </AuthShell>
  );
}
